import {
  type AccountPaymentMethod,
  type BankAccount,
  type AccountPaymentMethodBankAccount,
  Prisma,
  type Customer,
  type PaymentMethod,
} from "@prisma/client";
import { type ClassValue, clsx } from "clsx";
import Decimal from "decimal.js";
import { twMerge } from "tailwind-merge";

/**
 * Combines multiple class names into a single string.
 *
 * This function merges class names using `clsx` and `twMerge` to ensure that
 * Tailwind CSS classes are properly combined and deduplicated.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Creates a debounced function that delays invoking
 * the provided function until after the specified timeout.
 */
export function debounce<T>(func: (arg: T) => void, timeout: number) {
  let timer: NodeJS.Timeout;
  return (arg: T) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(arg);
    }, timeout);
  };
}

/**
 * Retrieves the initials of a customer based on their first and last name.
 */
export function getCustomerInitials(
  customer: Customer | Omit<Customer, "createdAt" | "updatedAt"> | undefined,
) {
  if (customer) {
    if (customer.firstName && customer.lastName) {
      return `${customer?.firstName[0]}${customer?.lastName[0]}`;
    } else if (customer.firstName) {
      return `${customer?.firstName[0]}`;
    }
  }
  return "";
}

/**
 * Retrieves the initials of a customer/student based on their first and last name.
 */
export function getInitials(entity?: { firstName: string; lastName: string }) {
  if (entity) {
    if (entity.firstName && entity.lastName) {
      return `${entity?.firstName[0]}${entity?.lastName[0]}`;
    } else if (entity.firstName) {
      return `${entity?.firstName[0]}`;
    }
  }
  return "";
}

/**
 * Splits an array into chunks of the specified size.
 */
export function chunk<T>(array: T[], size: number): T[][] {
  const results: T[][] = [];
  while (array.length) {
    results.push(array.splice(0, size));
  }
  return results;
}

export type TransformDecimalToNumber<T> = T extends Prisma.Decimal | Decimal
  ? number
  : T extends Date
    ? Date
    : T extends (infer U)[]
      ? TransformDecimalToNumber<U>[]
      : T extends object
        ? { [K in keyof T]: TransformDecimalToNumber<T[K]> }
        : T;

/**
 * Transforms Decimal values in an object to numbers.
 *
 * This function recursively traverses the object and converts Decimal values
 * to their numeric equivalents. It also preserves Date objects and arrays.
 */
export function transformDecimalToNumber<T>(
  obj: T,
): TransformDecimalToNumber<T> {
  if (obj === null || obj === undefined)
    return obj as TransformDecimalToNumber<T>;

  // Check for Prisma.Decimal
  if (obj instanceof Prisma.Decimal) {
    return Number(obj.toString()) as TransformDecimalToNumber<T>;
  }

  // Check for decimal.js Decimal
  if (obj instanceof Decimal) {
    return obj.toNumber() as TransformDecimalToNumber<T>;
  }

  // Preserve Date objects
  if (obj instanceof Date) {
    return obj as TransformDecimalToNumber<T>;
  }

  if (Array.isArray(obj)) {
    return obj.map(transformDecimalToNumber) as TransformDecimalToNumber<T>;
  }

  if (typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        transformDecimalToNumber(value),
      ]),
    ) as TransformDecimalToNumber<T>;
  }

  return obj as TransformDecimalToNumber<T>;
}

type AccountPaymentMethodWithBankAccounts = AccountPaymentMethod & {
  bankAccounts: (AccountPaymentMethodBankAccount & {
    bankAccount: BankAccount;
  })[];
};

/**
 * Groups account payment methods by method and bank accounts, removing duplicates.
 *
 * This function takes an array of account payment methods and groups them by
 * method, associating each method with a map of bank accounts.
 *
 * @param accountPaymentMethods - The array of account payment methods to group.
 * @returns An array of objects, each containing a method and its associated bank accounts.
 */
export function groupAccountPaymentMethodsByMethod(
  accountPaymentMethods: AccountPaymentMethodWithBankAccounts[],
) {
  const grouped: Record<string, Map<string, BankAccount>> = {};

  for (const apm of accountPaymentMethods) {
    const method = apm.method;

    if (!grouped[method]) {
      grouped[method] = new Map();
    }

    for (const apmBankAccount of apm.bankAccounts) {
      const bankAccount = apmBankAccount.bankAccount;
      grouped[method].set(bankAccount.id, bankAccount);
    }
  }

  // Convert to array
  return Object.entries(grouped).map(([method, bankAccountsMap]) => ({
    method: method as PaymentMethod,
    bankAccounts: Array.from(bankAccountsMap.values()),
  }));
}
